import React, { useState } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  CardText,
} from "reactstrap";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { DashboardCounter, addCandidateResume } from "./PartnerSlice";
import dropzoneimage2 from "../../src/assets/images/companyBenchImage/icons8-upload-to-the-cloud.gif";
import { log_debug } from "../apiHelpers/logger";
import Spinner from "../customComponents/utilities/Spinner";
import { toast } from "react-toastify";
import CustomBreadcrumb from "../customComponents/breadcrumb/CustomBreadcrumb";
import moment from "moment";

function AddCandidate() {
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [currentFileName, setCurrentFileName] = useState("");
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Date range for dashboard counter
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 90);
  
  const formattedStartDate = moment(defaultStartDate).format("YYYY-MM-DD");
  const formattedEndDate = moment(defaultEndDate).format("YYYY-MM-DD");

  // Dummy data fallback
  const dummyResponse = {
    candidates: {
      data: {
        resumes: 
          {
            full_name: "",
            date_of_birth: "",
            email_id: "",
            contact_no: "",
            address: "",
            work_experiences: [],
            educational_qualifications: [],
            certifications: [],
            linkedin_url: "",
            summary: "",
            resume_url: "",
            role: "",
            availability: "",
            monthly_salary: "",
            relevant_experience: "",
            prefered_locations: [],
            current_location: "",
            notice_period: "",
            experience: "",
            programming_languages: [],
          },
      },
    },
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Retry helper function with exponential backoff
  const retryWithDelay = (fn, maxRetries = 3, baseDelay = 1000) => {
    return new Promise((resolve, reject) => {
      const attempt = async (retryAttempt) => {
        try {
          const result = await fn();
          resolve(result);
        } catch (error) {
          if (retryAttempt < maxRetries) {
            const delay = baseDelay * Math.pow(2, retryAttempt); // Exponential backoff
            log_debug(`Attempt ${retryAttempt + 1} failed, retrying in ${delay}ms...`);
            
            setTimeout(() => {
              attempt(retryAttempt + 1);
            }, delay);
          } else {
            reject(error);
          }
        }
      };
      attempt(0);
    });
  };

  // Handle single file upload with retry logic
  const uploadFile = async (file) => {
    setUploading(true);
    setCurrentFileName(file.name);
    setRetryCount(0);
    
    try {
      log_debug("Processing resume file:", file.name);

      // Convert to base64
      const base64File = await convertToBase64(file);
      const payload = {
        base64: base64File.split(",")[1],
      };

      // Upload with retry logic
      const response = await retryWithDelay(
        async () => {
          setRetryCount(prev => prev + 1);
          log_debug(`Upload attempt ${retryCount + 1} for file: ${file.name}`);
          
          if (retryCount > 0) {
            toast.info(`Retrying upload... Attempt ${retryCount + 1}/3`);
          }
          
          const result = await dispatch(addCandidateResume(payload));
          
          // Check if response is valid
          if (!result?.payload?.data?.resumes) {
            throw new Error("Invalid response format or parsing failed");
          }
          
          return result;
        },
        3, // Max 3 attempts
        1500 // Base delay of 1.5 seconds
      );

      // Success - update dashboard counter and navigate
      setUploadedFile({
        name: file.name,
        status: "success",
        data: response.payload
      });

      dispatch(
        DashboardCounter({
          from_date: formattedStartDate,
          to_date: formattedEndDate,
        })
      );

      toast.success("Resume uploaded and parsed successfully!");
      
      // Navigate to candidate details
      navigate("/candidatedetails", {
        state: { 
          individualResumes: [{
            fileName: file.name,
            status: "success",
            candidates: response.payload,
          }]
        },
      });

    } catch (error) {
      log_debug("All upload attempts failed:", error);
      
      setUploadedFile({
        name: file.name,
        status: "error",
        error: error.message
      });

      toast.error(`Failed to parse resume.`);
      
      // Navigate with dummy data after all retries failed
      setTimeout(() => {
        navigate("/candidatedetails", {
          state: { 
            individualResumes: [{
              fileName: file.name,
              status: "success",
              candidates: dummyResponse?.candidates,
            }]
          },
        });
      }, 2000);
    } finally {
      setUploading(false);
      setRetryCount(0);
      setCurrentFileName("");
    }
  };

  // Handle file drop
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) {
      toast.error("Please select a valid file (.pdf, .docx, .doc)");
      return;
    }

    // Take only the first file
    const file = acceptedFiles[0];
    
    // Validate file type
    const validExtensions = ['.pdf', '.docx', '.doc'];
    const isValidFile = validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );

    if (!isValidFile) {
      toast.error("Please upload only PDF, DOCX, or DOC files");
      return;
    }

    await uploadFile(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    multiple: false, // Only allow single file
    maxFiles: 1
  });

  const breadcrumbValues = ["Candidates", "Add Candidate"];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div>
            <div className="col-lg-12">
              <CustomBreadcrumb values={breadcrumbValues} />

              <Col className="col-lg-12 col-md-12 col-sm-12">
                <Row>
                  <Col
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Card
                      style={{
                        width: "60%",
                        height: "auto",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <CardHeader
                        className="text-center bg-primary-subtle font-size-22"
                        style={{ height: "100px" }}
                      >
                        <strong className="text-white">Upload</strong>
                        <span className="text-muted"> Resume</span>
                      </CardHeader>
                      <CardBody>
                        <CardText className="mb-0">
                          <div className="">
                            <div
                              {...getRootProps()}
                              className={`dropzone ${
                                isDragActive ? "active" : ""
                              }`}
                              style={{
                                border: "2px dashed #ccc",
                                borderRadius: "8px",
                                padding: "40px",
                                textAlign: "center",
                                cursor: "pointer",
                                transition: "border-color 0.3s ease"
                              }}
                            >
                              <input {...getInputProps()} disabled={uploading} />
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                {uploading ? (
                                  <div style={{ textAlign: "center" }}>
                                    <Spinner />
                                    {retryCount > 0 && (
                                      <p style={{ marginTop: "10px", fontSize: "14px" }}>
                                        {/* <strong>Retry attempt {retryCount}/3</strong>
                                        <br /> */}
                                        <small className="text-muted">
                                          Processing: {currentFileName}
                                        </small>
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    <p style={{ marginBottom: "16px", fontSize: "16px" }}>
                                      <strong>Drag and drop</strong> a resume here{" "}
                                      <br /> or <strong>click to browse</strong>
                                      <br />
                                      <small className="text-muted">
                                        Accepts .pdf, .docx, .doc files only
                                      </small>
                                    </p>

                                    <img
                                      src={dropzoneimage2}
                                      style={{ width: "70px" }}
                                      alt="Upload Icon"
                                    />
                                    
                                    {uploadedFile && (
                                      <div className="mt-3">
                                        <span className={`badge ${
                                          uploadedFile.status === 'success' 
                                            ? 'bg-success' 
                                            : 'bg-danger'
                                        }`}>
                                          {uploadedFile.name} - {uploadedFile.status}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardText>
                        <div className="py-4">
                          <p className="bg-info px-3 rounded py-3 mb-0">
                            <i className="fas fa-info-circle me-2"></i>
                            By uploading this resume, you confirm that you have
                            obtained the necessary consent from the candidate.
                            All information will be used solely for recruitment
                            purposes and will not be shared elsewhere.
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default AddCandidate;